import React, { useEffect, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ListTodo } from 'lucide-react';
import Text from 'components/text';
import api from 'utils/api';
import { PublicCourse } from './types';

const PublicCourseCard = ({ course }: { course: PublicCourse }) => (
  <Box
    padding={2}
    paddingTop={3}
    paddingBottom={3}
    border={1}
    borderRadius={2}
    borderColor="#ccc"
    height="100%"
  >
    <Box
      component={Link}
      to={`/pub/edu/${course.slug}`}
      display="flex"
      gap={2}
      sx={{ textDecoration: 'none', '&:hover': { opacity: 0.8 } }}
    >
      <ListTodo size={28} color="#888" />
      <Text fontSize={24} color="#555" letterSpacing={0.5} lineHeight={1.1}>
        {course.title}
      </Text>
    </Box>
    <Text color="#666" fontSize={18} paddingY={2}>
      {course.description}
    </Text>
    <Box display="flex" flexDirection="column" gap={1}>
      {course.modules.map((module) => (
        <Box
          key={module.slug}
          component={Link}
          to={`/pub/edu/${course.slug}/${module.slug}`}
          color="#555"
          fontSize={18}
          sx={{ textDecoration: 'underline', '&:hover': { color: '#000' } }}
        >
          {module.title}
        </Box>
      ))}
    </Box>
  </Box>
);

/** Entry point for guests: every public course with its public modules (GET /edu/public). */
const PublicEducation: React.FC = () => {
  const { t } = useTranslation();
  const [courses, setCourses] = useState<PublicCourse[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.edu
      .getPublic<PublicCourse[]>()
      .then(setCourses)
      .catch(() => setError(true));
  }, []);

  return (
    <Grid container flexDirection="column" className="container mx-auto">
      <Box paddingX="20px" paddingY={4} borderBottom={1} borderColor="#eee">
        <Text fontSize={24} paddingBottom={1}>
          {t('Education')}
        </Text>
        <Text color="#666" fontSize={18}>
          {t('Free modules by Snafu. Registered artists get the full courses.')}
        </Text>
      </Box>

      <Box paddingX="20px" paddingY={4}>
        {error && (
          <Text>
            {t(
              'Sorry, the page you are looking for does not exist, please contact with us if you are facing an issues.'
            )}
          </Text>
        )}
        {!error && !courses && <Text>{t('Loading....')}</Text>}
        {courses && (
          <Grid container spacing={4}>
            {courses.map((course) => (
              <Grid item xs={12} md={6} key={course.slug}>
                <PublicCourseCard course={course} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Grid>
  );
};

export default PublicEducation;
